/**
 * Layer-source helpers shared by the per-app `layers.ts` builders.
 */

import type { IFeature, ILayerSource, TLayer } from '#lib/types.js';
import { tmsVectorTiles } from '#lib/components/map/tileTemplate.js';

type BBox = NonNullable<ILayerSource['bbox']>;

export const bboxTuple = ({ minx, miny, maxx, maxy }: BBox): [number, number, number, number] => [
	minx,
	miny,
	maxx,
	maxy
];

/**
 * Features of `dataset` grouped by their `module` keyword and tagged with `product`.
 * `appLayers` is expected to already be filtered to the active version (see `loadSystemLayers`).
 */
export function groupByModule<F extends IFeature>(
	appLayers: F[],
	product: F['product'],
	dataset: string
): Partial<Record<F['module'], F[]>> | undefined {
	type Modules = Partial<Record<F['module'], F[]>>;
	let modules: Modules | undefined;
	for (const layer of appLayers) {
		const module = layer.keywords.module as F['module'] | undefined;
		if (layer.dataset !== dataset || module === undefined) continue;
		((modules ??= {} as Modules)[module] ??= []).push({
			...layer,
			product,
			module,
			detail: layer.keywords.detail
		});
	}
	return modules;
}

/**
 * Vector-tile layer source for an operational feature. Sources are keyed per layer
 * unless `sharedSource`, which keys per feature so layers sharing one share a source.
 */
export function getVectorLayerSource(
	layerId: string,
	feature: IFeature | undefined,
	layerProps: Partial<TLayer>,
	zIndex: number,
	{ selectable, sharedSource = false }: { selectable?: boolean; sharedSource?: boolean } = {}
): ILayerSource | null {
	if (!feature) return null;
	return {
		id: layerId,
		zIndex,
		title: feature.title,
		detail: feature.detail,
		abstract: feature.abstract?.trim() || undefined,
		bbox: feature.latLonBbox,
		selectable,
		source: {
			type: 'vector',
			...tmsVectorTiles(feature.xyzTemplate),
			bounds: bboxTuple(feature.latLonBbox),
			attribution: feature.attribution
		},
		layer: {
			...layerProps,
			id: `${feature.layer}|${layerId}`,
			source: `${sharedSource ? feature.layer : layerId}-source`,
			'source-layer': feature.layer
		}
	};
}
